import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { ownUsageSchema } from "./account-usage";
import { publicIdSchema } from "./public-id";

const count = z.int().nonnegative();
const amount = z.number().nonnegative();
const date = z.iso.date();
export const adminOverviewSchema = z.strictObject({
  newUsers7d: count,
  newUsers30d: count,
  paying: count,
  free: count,
  active7d: count,
  daily: z
    .array(
      z.strictObject({
        date,
        signups: count,
        recordingMinutes: amount,
        aiActions: count,
      }),
    )
    .length(30),
  statusSource: z.literal("stored"),
});
const usersCursorTuple = z.tuple([z.iso.datetime().nullable(), publicIdSchema]);
export function decodeAdminUsersCursor(cursor: string) {
  try {
    if (!/^[A-Za-z0-9_-]+$/.test(cursor)) return undefined;
    const decoded = JSON.parse(
      atob(cursor.replaceAll("-", "+").replaceAll("_", "/")),
    );
    const result = usersCursorTuple.safeParse(decoded);
    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
}
export const adminUsersQuerySchema = z.strictObject({
  cursor: z
    .string()
    .min(1)
    .max(1024)
    .refine((cursor) => decodeAdminUsersCursor(cursor) !== undefined, {
      message: "Invalid users cursor",
    })
    .optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  email: z.string().max(255).optional(),
});
const auditCursorTuple = z.tuple([z.iso.datetime(), z.uuid()]);
export function decodeAdminAuditCursor(cursor: string) {
  try {
    if (!/^[A-Za-z0-9_-]+$/.test(cursor)) return undefined;
    const result = auditCursorTuple.safeParse(
      JSON.parse(atob(cursor.replaceAll("-", "+").replaceAll("_", "/"))),
    );
    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
}
export const adminAuditQuerySchema = z.strictObject({
  cursor: z
    .string()
    .min(1)
    .max(1024)
    .refine((cursor) => decodeAdminAuditCursor(cursor) !== undefined, {
      message: "Invalid audit cursor",
    })
    .optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  actor: z.email().max(255).optional(),
  target: publicIdSchema.optional(),
});
export const adminAuditSchema = z.strictObject({
  items: z.array(
    z.strictObject({
      auditId: z.uuid(),
      actorEmail: z.string().nullable(),
      actorKind: z.enum(["admin", "bootstrap"]),
      action: z.string(),
      publicId: publicIdSchema.nullable(),
      reason: z.string().nullable(),
      outcome: z.enum(["success", "denied", "failed"]),
      createdAt: z.iso.datetime(),
    }),
  ),
  nextCursor: z.string().nullable(),
});
const adminAccountSchema = z.strictObject({
  publicId: publicIdSchema,
  email: z.string(),
  createdAt: z.string().nullable(),
  planName: z.string().nullable(),
  planStatus: z.string().nullable(),
  isAdmin: z.boolean(),
  banned: z.boolean(),
  deletionRequested: z.boolean(),
  hasLogin: z.boolean(),
});
export const adminUsersSchema = z.strictObject({
  items: z.array(
    adminAccountSchema.extend({ minutes30d: amount, aiCostUsd30d: amount }),
  ),
  nextCursor: z.string().nullable(),
});
export const adminUserSchema = z.strictObject({
  account: adminAccountSchema.extend({
    banReason: z.string().nullable(),
    twoFactorEnabled: z.boolean(),
    emailVerified: z.boolean(),
    loginEmail: z.string().nullable(),
  }),
  usage: ownUsageSchema,
  dailyMinutes: z.array(z.strictObject({ date, minutes: amount })).length(30),
  aiByTool: z.array(
    z.strictObject({
      tool: z.string(),
      actions: count,
      inputTokens: count,
      outputTokens: count,
      costUsd: amount,
    }),
  ),
  lastRecordingAt: z.string().nullable(),
});
const adminRead = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  NOT_FOUND: { message: "Not Found" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Admin required" },
  ADMIN_STEP_UP_REQUIRED: { status: 401, message: "Admin step-up required" },
  SERVICE_UNAVAILABLE: { message: "Authentication unavailable" },
});
const adminRole = adminRead
  .errors({
    LAST_ADMIN: { status: 409, message: "The last admin cannot be revoked" },
    ADMIN_TARGET_NO_LOGIN: { status: 409, message: "Target has no login" },
    ADMIN_FACTOR_REQUIRED: {
      status: 409,
      message: "Target needs a verified authenticator app or passkey",
    },
  })
  .input(
    z.object({
      params: z.strictObject({ publicId: publicIdSchema }),
      body: z.strictObject({
        role: z.enum(["admin", "user"]),
        reason: z.string().trim().min(3).max(1000),
      }),
    }),
  )
  .meta(
    openapi({
      method: "POST",
      path: "/api/v2/admin/users/{publicId}/role",
      inputStructure: "detailed",
      spec: (spec) => ({ ...spec, security: [{ cookieAuth: [] }] }),
    }),
  )
  .output(z.strictObject({ role: z.enum(["admin", "user"]) }));
const adminAccountAction = adminRead
  .errors({
    TARGET_IS_ADMIN: { status: 409, message: "Revoke admin before banning" },
    ADMIN_TARGET_NO_LOGIN: { status: 409, message: "Target has no login" },
  })
  .input(
    z.object({
      params: z.strictObject({ publicId: publicIdSchema }),
      body: z.strictObject({
        reason: z.string().trim().min(3).max(1000),
      }),
    }),
  );
const adminBan = adminAccountAction.output(
  z.strictObject({ banned: z.boolean() }),
);
export const adminContract = {
  adminSetPassword: adminRead
    .errors({
      ADMIN_TARGET_NO_LOGIN: { status: 409, message: "Target has no login" },
    })
    .input(
      z.object({
        params: z.strictObject({ publicId: publicIdSchema }),
        body: z.strictObject({
          password: z.string().min(1),
          reason: z.string().trim().min(3).max(1000),
        }),
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/admin/users/{publicId}/password",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ cookieAuth: [] }] }),
      }),
    )
    .output(z.strictObject({ success: z.boolean() })),
  adminImpersonate: adminAccountAction
    .errors({
      TARGET_IS_ADMIN: {
        status: 409,
        message: "Admins cannot be impersonated",
      },
    })
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/admin/users/{publicId}/impersonate",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ cookieAuth: [] }] }),
      }),
    )
    .output(z.strictObject({ success: z.boolean() })),
  adminRemove: adminAccountAction
    .errors({
      TARGET_IS_ADMIN: {
        status: 409,
        message: "Revoke admin before removing",
      },
      SECURITY_RECONCILIATION_REQUIRED: {
        status: 503,
        message: "Account access denied; security reconciliation required",
      },
    })
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/admin/users/{publicId}/remove",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ cookieAuth: [] }] }),
      }),
    )
    .output(z.strictObject({ success: z.boolean() })),
  adminBan: adminBan.meta(
    openapi({
      method: "POST",
      path: "/api/v2/admin/users/{publicId}/ban",
      inputStructure: "detailed",
      spec: (spec) => ({ ...spec, security: [{ cookieAuth: [] }] }),
    }),
  ),
  adminUnban: adminBan.meta(
    openapi({
      method: "POST",
      path: "/api/v2/admin/users/{publicId}/unban",
      inputStructure: "detailed",
      spec: (spec) => ({ ...spec, security: [{ cookieAuth: [] }] }),
    }),
  ),
  // Both client actions use the same role endpoint and body.
  adminGrantRole: adminRole,
  adminRevokeRole: adminRole,
  adminAudit: adminRead
    .input(
      z.object({
        query: adminAuditQuerySchema.default({ limit: 25 }),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/admin/audit",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ cookieAuth: [] }] }),
      }),
    )
    .output(adminAuditSchema),
  adminOverview: adminRead
    .input(z.strictObject({}))
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/admin/overview",
        spec: (spec) => ({ ...spec, security: [{ cookieAuth: [] }] }),
      }),
    )
    .output(adminOverviewSchema),
  adminUsers: adminRead
    .input(
      z.object({
        query: adminUsersQuerySchema.default({ limit: 25 }),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/admin/users",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ cookieAuth: [] }] }),
      }),
    )
    .output(adminUsersSchema),
  adminUser: adminRead
    .input(
      z.object({
        params: z.strictObject({ publicId: publicIdSchema }),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/admin/users/{publicId}",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ cookieAuth: [] }] }),
      }),
    )
    .output(adminUserSchema),
};
